import React, { useState, useEffect } from 'react';
import { staffService, featureFlagsService, adminService } from '../services/api';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Users, 
  Shield, 
  UserPlus,
  AlertTriangle,
  RefreshCw,
  Trash2,
  Eye
} from 'lucide-react';

const StaffManagement = () => {
  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('assignments');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [assignNotes, setAssignNotes] = useState('');

  useEffect(() => {
    checkFeatureAndLoad();
  }, []);

  const checkFeatureAndLoad = async () => {
    try {
      const enabledFeatures = await featureFlagsService.getEnabledFeatures();
      const isEnabled = enabledFeatures.staff_roles_enabled === true;
      setFeatureEnabled(isEnabled);
      
      if (isEnabled) {
        await loadData();
      }
    } catch (err) {
      setFeatureEnabled(false);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      const [rolesData, assignmentsData, usersData] = await Promise.all([
        staffService.getRoles().catch(() => ({ roles: [] })),
        staffService.getAssignments().catch(() => ({ assignments: [] })),
        adminService.getUsers().catch(() => ({ users: [] }))
      ]);
      
      setAvailableRoles(rolesData.roles || []);
      setAssignments(assignmentsData.assignments || []);
      setUsers(usersData.users || []);
    } catch (err) {
      console.error('Failed to load staff data:', err);
    }
  };

  const handleAssignRole = async () => {
    if (!selectedUser || !selectedRole) {
      alert('Please select a user and role');
      return;
    }
    try {
      await staffService.assignRole({
        user_id: selectedUser,
        role: selectedRole,
        notes: assignNotes
      });
      alert('Role assigned successfully!');
      setShowAssignModal(false);
      setSelectedUser(null);
      setSelectedRole('');
      setAssignNotes('');
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail?.message || err.response?.data?.detail || 'Failed to assign role');
    }
  };

  const handleRevokeRole = async (assignmentId) => {
    const reason = prompt('Reason for revoking this role:');
    if (!reason) return;
    try {
      await staffService.revokeRole(assignmentId, reason);
      alert('Role revoked successfully');
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to revoke role');
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      dispatcher: 'bg-blue-100 text-blue-800',
      fleet_manager: 'bg-purple-100 text-purple-800',
      mechanic: 'bg-orange-100 text-orange-800',
      accounting: 'bg-green-100 text-green-800',
      operations_admin: 'bg-indigo-100 text-indigo-800',
      super_admin: 'bg-red-100 text-red-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!featureEnabled) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto p-6">
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-8 h-8 text-yellow-600 flex-shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold text-yellow-800">Feature Not Enabled</h2>
                  <p className="text-yellow-700 mt-2">
                    Staff Roles Management is currently disabled. Contact your administrator to enable this feature in System Settings → Feature Toggles.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Staff Management</h1>
            <p className="text-gray-600 mt-1">Manage staff roles and permissions</p>
          </div>
          <Button onClick={() => setShowAssignModal(true)} className="flex items-center gap-2">
            <UserPlus className="w-4 h-4" /> Assign Role
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-blue-600" />
                <div>
                  <div className="text-2xl font-bold">{assignments.length}</div>
                  <div className="text-sm text-gray-500">Active Assignments</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Shield className="w-8 h-8 text-purple-600" />
                <div>
                  <div className="text-2xl font-bold">{availableRoles.length}</div>
                  <div className="text-sm text-gray-500">Available Roles</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex border-b mb-6">
          {['assignments', 'roles'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium capitalize ${
                activeTab === tab 
                  ? 'text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'assignments' ? 'Staff Assignments' : 'Available Roles'}
            </button>
          ))}
        </div>

        {activeTab === 'assignments' && (
          <Card>
            <CardHeader>
              <CardTitle>Current Staff Assignments</CardTitle>
            </CardHeader>
            <CardContent>
              {assignments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No staff assignments yet.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2">User</th>
                        <th className="text-left py-3 px-2">Role</th>
                        <th className="text-left py-3 px-2">Assigned By</th>
                        <th className="text-left py-3 px-2">Assigned At</th>
                        <th className="text-left py-3 px-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assignments.map(assignment => (
                        <tr key={assignment.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-2">
                            <div className="font-medium">{assignment.user_name}</div>
                            <div className="text-xs text-gray-500">{assignment.user_email}</div>
                          </td>
                          <td className="py-3 px-2">
                            <Badge className={getRoleBadgeColor(assignment.role)}>
                              {assignment.role?.replace(/_/g, ' ').toUpperCase()}
                            </Badge>
                          </td>
                          <td className="py-3 px-2">{assignment.assigned_by_name}</td>
                          <td className="py-3 px-2 text-xs">
                            {new Date(assignment.assigned_at).toLocaleDateString()}
                          </td>
                          <td className="py-3 px-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRevokeRole(assignment.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'roles' && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {availableRoles.map(role => (
              <Card key={role.key}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    {role.name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Can View:</span>
                      <div className="text-gray-500">{role.permissions?.can_view?.join(', ') || 'None'}</div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Can Edit:</span>
                      <div className="text-gray-500">{role.permissions?.can_edit?.join(', ') || 'None'}</div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Restricted From:</span>
                      <div className="text-red-500">{role.permissions?.restricted_from?.join(', ') || 'None'}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Assign Role Modal */}
        {showAssignModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-lg font-semibold mb-4">Assign Staff Role</h3>
              <div className="space-y-4">
                <div>
                  <Label>Select User *</Label>
                  <select
                    value={selectedUser || ''}
                    onChange={(e) => setSelectedUser(e.target.value)}
                    className="w-full border rounded-md p-2"
                  >
                    <option value="">Choose a user...</option>
                    {users.filter(u => !['admin', 'super_admin'].includes(u.role)).map(user => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name} ({user.email})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Select Role *</Label>
                  <select
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    className="w-full border rounded-md p-2"
                  >
                    <option value="">Choose a role...</option>
                    <option value="dispatcher">Dispatcher</option>
                    <option value="fleet_manager">Fleet Manager</option>
                    <option value="mechanic">Mechanic</option>
                    <option value="accounting">Accounting</option>
                    <option value="operations_admin">Operations Admin</option>
                  </select>
                </div>
                <div>
                  <Label>Notes (optional)</Label>
                  <Input
                    value={assignNotes}
                    onChange={(e) => setAssignNotes(e.target.value)}
                    placeholder="Assignment notes..."
                  />
                </div>
              </div>
              <div className="flex gap-3 justify-end mt-6">
                <Button variant="outline" onClick={() => setShowAssignModal(false)}>Cancel</Button>
                <Button onClick={handleAssignRole}>Assign Role</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default StaffManagement;
